import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router } from '@angular/router';

import { IntegrationsService } from '../../company-profile/integrations.service';
import { TrackActionSeenDirective } from '../../core/directives/track-action-seen.directive';
import { UserService } from '../../core/services/user.service';
import { NylasCategoryEnum } from '../../integrations/models/integrations-page.model';
import {
  NylasBannerContent,
  NylasBannerVideoContent,
  nylasPromoSessionKey,
  NylasScreenEnum,
  NylasScreenType,
} from '../../models/nylas-screen';
import { LogoComponent } from '../../ui-kit/logo/logo.component';
import { VimeoPlayerComponent } from '../../ui-kit/vimeo-player/vimeo-player.component';

@Component({
  selector: 'app-nylas-integration-banner',
  templateUrl: './nylas-integration-banner.component.html',
  styleUrls: ['./nylas-integration-banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [MatIconModule, MatButtonModule, TrackActionSeenDirective, VimeoPlayerComponent, LogoComponent],
})
export class NylasIntegrationBannerComponent implements OnInit {
  private integrationsService = inject(IntegrationsService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private userService = inject(UserService);
  private destroyRef = inject(DestroyRef);

  nylasTextMap: Record<NylasScreenType, NylasBannerContent> = {
    [NylasScreenEnum.COI]: {
      title: 'Forget COI requests. We’ve got it covered.',
      subtitle:
        'Never respond to another Certificate of Insurance request — we’ll handle it automatically. Constrafor auto-sends your COIs to GCs\n' +
        '          instantly. No follow-ups, no paperwork — just set it and forget it.',
      vimeoVideoId: 1123560987,
      buttonText: 'Automate COIs now',
    },
    [NylasScreenEnum.SOV]: {
      title: 'SOV automation for faster pay.',
      subtitle:
        'Constrafor validates line items, tracks revisions, and auto-submits your SOV so reviews move quicker and payments follow.',
      image: 'assets/nylas-promo/sov-nylas.png',
      buttonText: 'Automate SOV now',
    },
    [NylasScreenEnum.CONTRACTS]: {
      title: 'Forget contract uploads. We’ve got it covered.',
      subtitle:
        'Never chase down another contract submission — we’ll handle it automatically. Constrafor uploads your signed contracts to GCs instantly. No email chains, no missing attachments — just set it and forget it.',
      image: 'assets/nylas-promo/contracts-nylas.png',
      buttonText: 'Automate contracts now',
    },
    [NylasScreenEnum.INVOICE]: {
      title: 'Forget invoice uploads. We’ve got it covered.',
      subtitle:
        'Never waste time sending invoices manually — we’ll handle it automatically. Constrafor submits your invoices to GCs instantly. No follow-ups, no lost emails — just set it and forget it.',
      image: 'assets/nylas-promo/invoice-nylas.png',
      buttonText: 'Automate invoices now',
    },
    [NylasScreenEnum.PREQUALIFICATION]: {
      title: 'Skip prequal uploads. We’ll handle it.',
      subtitle:
        'Constrafor compiles and submits your prequalification package to GCs automatically—no portals, no follow-ups, faster approvals.',
      image: 'assets/nylas-promo/prequal-nylas.png',
      buttonText: 'Automate prequalification',
    },
    [NylasScreenEnum.PREQUAL]: {
      title: 'Skip prequal uploads. We’ll handle it.',
      subtitle:
        'Constrafor compiles and submits your prequalification package to GCs automatically—no portals, no follow-ups, faster approvals.',
      image: 'assets/nylas-promo/prequal-nylas.png',
      buttonText: 'Automate prequalification',
    },
    [NylasScreenEnum.DIVERSITY]: {
      title: 'Forget COI requests. We’ve got it covered.',
      subtitle:
        'Never respond to another Certificate of Insurance request — we’ll handle it automatically. Constrafor auto-sends your COIs to GCs\n' +
        '          instantly. No follow-ups, no paperwork — just set it and forget it.',
      vimeoVideoId: 1123560987,
      buttonText: 'Automate COIs now',
    },
  };
  source: string;
  bannerContent: NylasBannerContent;

  ngOnInit(): void {
    const paramSource = this.route.snapshot.queryParamMap.get('source');
    const source = paramSource in this.nylasTextMap ? paramSource : NylasScreenEnum.COI;
    this.userService
      .checkAndMarkPageAsVisited(`connect-nylas-promo-${source}`)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
    sessionStorage.setItem(nylasPromoSessionKey, '1');
    this.bannerContent = this.nylasTextMap[source];
    this.integrationsService.initNylasConnectionCheck();
  }

  connectNylas(): void {
    this.integrationsService.addNylasIntegration('growth', this.integrationsService.nylasBannerBackUrl, {
      category: NylasCategoryEnum.INSURANCE,
    });
  }

  navigateToPreviousPage(): void {
    this.router.navigateByUrl(this.integrationsService.nylasBannerBackUrl);
  }

  isVideoBanner(content: NylasBannerContent): content is NylasBannerVideoContent {
    return 'vimeoVideoId' in content;
  }
}
